// TransformationAnalyzer - Handles analysis and reporting of transformation results
export class TransformationAnalyzer {
  constructor() {
    this.analysisStats = {
      totalAnalyses: 0,
      dataStructuresAnalyzed: 0,
      summariesGenerated: 0,
      reportsCreated: 0
    };
    
    this.analysisHistory = [];
  }

  // Analyze data structure for debugging and insights
  analyzeDataStructure(data) {
    this.analysisStats.totalAnalyses++;
    this.analysisStats.dataStructuresAnalyzed++;
    
    const analysis = {
      timestamp: new Date().toISOString(),
      totalCategories: 0,
      totalControlItems: 0,
      totalChecks: 0,
      failedChecks: 0,
      passedChecks: 0,
      dataFormat: 'unknown',
      isIndependent: true,
      structureMap: {},
      dataIntegrity: {
        hasOrphanedChecks: false,
        hasEmptyCategories: false,
        hasEmptyControlItems: false,
        consistencyScore: 100,
        issues: []
      },
      dataQuality: {
        completenessScore: 0,
        richnessScore: 0,
        consistencyScore: 0,
        overallScore: 0
      },
      patterns: {
        categoryPatterns: {},
        controlItemPatterns: {},
        checkPatterns: {}
      }
    };

    if (!data || typeof data !== 'object') {
      analysis.dataIntegrity.consistencyScore = 0;
      analysis.dataIntegrity.issues.push('Invalid data: not an object');
      return this.finalizeAnalysis(analysis);
    }

    analysis.dataFormat = this.detectDataFormat(data);
    analysis.totalCategories = Object.keys(data).length;

    // Analyze each category
    Object.keys(data).forEach(categoryId => {
      const categoryData = data[categoryId];
      
      const categoryAnalysis = this.analyzeCategoryStructure(categoryId, categoryData, analysis.dataFormat);
      
      // Update totals
      analysis.totalControlItems += categoryAnalysis.controlItemCount;
      analysis.totalChecks += categoryAnalysis.checkCount;
      analysis.failedChecks += categoryAnalysis.failedChecks;
      analysis.passedChecks += categoryAnalysis.passedChecks;
      
      // Store structure mapping
      analysis.structureMap[categoryId] = categoryAnalysis.structure;
      
      // Update integrity issues
      analysis.dataIntegrity.issues.push(...categoryAnalysis.issues);
      
      // Update patterns
      this.updatePatterns(analysis.patterns, categoryAnalysis);
    });

    // Calculate integrity scores
    this.calculateIntegrityScores(analysis);
    
    // Calculate quality scores
    this.calculateQualityScores(analysis, data);
    
    // Store in history
    this.analysisHistory.push({
      timestamp: analysis.timestamp,
      summary: {
        totalCategories: analysis.totalCategories,
        totalChecks: analysis.totalChecks,
        failedChecks: analysis.failedChecks,
        dataFormat: analysis.dataFormat,
        overallScore: analysis.dataQuality.overallScore
      }
    });
    
    // Keep only last 50 analyses
    if (this.analysisHistory.length > 50) {
      this.analysisHistory = this.analysisHistory.slice(-50);
    }

    console.log('📊 Independent Data Structure Analysis completed:', {
      categories: analysis.totalCategories,
      checks: analysis.totalChecks,
      failed: analysis.failedChecks,
      format: analysis.dataFormat,
      quality: analysis.dataQuality.overallScore
    });
    
    return this.finalizeAnalysis(analysis);
  }

  // Analyze individual category structure
  analyzeCategoryStructure(categoryId, categoryData, dataFormat) {
    const categoryAnalysis = {
      controlItemCount: 0,
      checkCount: 0,
      failedChecks: 0,
      passedChecks: 0,
      structure: {
        controlItems: {},
        hasData: false,
        categoryInfo: this.extractCategoryInfo(categoryData)
      },
      issues: [],
      patterns: {
        controlItemTypes: {},
        checkTypes: {},
        dataCompleteness: {}
      }
    };

    if (!categoryData || typeof categoryData !== 'object') {
      categoryAnalysis.issues.push(`Category ${categoryId} is not an object`);
      return categoryAnalysis;
    }

    if (!categoryData.controlItems) {
      categoryAnalysis.issues.push(`Category ${categoryId} has no control items`);
      return categoryAnalysis;
    }

    const controlItemKeys = Object.keys(categoryData.controlItems);
    categoryAnalysis.controlItemCount = controlItemKeys.length;

    if (controlItemKeys.length === 0) {
      categoryAnalysis.issues.push(`Category ${categoryId} has empty control items`);
      return categoryAnalysis;
    }

    // Analyze each control item
    controlItemKeys.forEach(controlItemId => {
      const controlItemData = categoryData.controlItems[controlItemId];
      
      const controlItemAnalysis = this.analyzeControlItemStructure(
        controlItemId, controlItemData, dataFormat
      );
      
      // Update counts
      categoryAnalysis.checkCount += controlItemAnalysis.checkCount;
      categoryAnalysis.failedChecks += controlItemAnalysis.failedChecks;
      categoryAnalysis.passedChecks += controlItemAnalysis.passedChecks;
      
      // Store structure
      categoryAnalysis.structure.controlItems[controlItemId] = controlItemAnalysis.structure;
      
      // Update issues
      categoryAnalysis.issues.push(...controlItemAnalysis.issues);
      
      if (controlItemAnalysis.structure.hasData) {
        categoryAnalysis.structure.hasData = true;
      }
      
      // Update patterns
      this.updateControlItemPatterns(categoryAnalysis.patterns, controlItemAnalysis);
    });

    return categoryAnalysis;
  }

  // Analyze individual control item structure
  analyzeControlItemStructure(controlItemId, controlItemData, dataFormat) {
    const controlItemAnalysis = {
      checkCount: 0,
      failedChecks: 0,
      passedChecks: 0,
      structure: {
        checks: {},
        hasData: false,
        controlItemInfo: this.extractControlItemInfo(controlItemData)
      },
      issues: [],
      patterns: {
        checkStatuses: {},
        dataTypes: {},
        completeness: {}
      }
    };

    if (!controlItemData || typeof controlItemData !== 'object') {
      controlItemAnalysis.issues.push(`Control item ${controlItemId} is not an object`);
      return controlItemAnalysis;
    }

    if (!controlItemData.checks) {
      controlItemAnalysis.issues.push(`Control item ${controlItemId} has no checks`);
      return controlItemAnalysis;
    }

    const checkKeys = Object.keys(controlItemData.checks);
    controlItemAnalysis.checkCount = checkKeys.length;

    if (checkKeys.length === 0) {
      controlItemAnalysis.issues.push(`Control item ${controlItemId} has empty checks`);
      return controlItemAnalysis;
    }

    // Analyze each check
    checkKeys.forEach(checkId => {
      const checkData = controlItemData.checks[checkId];
      
      const checkAnalysis = this.analyzeCheckStructure(checkId, checkData, dataFormat);
      
      // Update counts based on format
      if (dataFormat === 'cumulative') {
        if (checkData.isChecked) {
          controlItemAnalysis.failedChecks++;
        } else {
          controlItemAnalysis.passedChecks++;
        }
      } else {
        if (checkData.isCompleted) {
          if (checkData.isChecked) {
            controlItemAnalysis.failedChecks++;
          } else {
            controlItemAnalysis.passedChecks++;
          }
        }
      }
      
      // Store structure
      controlItemAnalysis.structure.checks[checkId] = checkAnalysis.structure;
      
      // Update issues
      controlItemAnalysis.issues.push(...checkAnalysis.issues);
      
      if (checkAnalysis.structure.hasData) {
        controlItemAnalysis.structure.hasData = true;
      }
      
      // Update patterns
      this.updateCheckPatterns(controlItemAnalysis.patterns, checkAnalysis);
    });

    return controlItemAnalysis;
  }

  // Analyze individual check structure
  analyzeCheckStructure(checkId, checkData, dataFormat) {
    const checkAnalysis = {
      structure: {
        hasData: false,
        dataCompleteness: 0,
        availableFields: [],
        missingFields: [],
        checkInfo: this.extractCheckInfo(checkData)
      },
      issues: [],
      patterns: {
        fieldTypes: {},
        dataQuality: {}
      }
    };

    if (!checkData || typeof checkData !== 'object') {
      checkAnalysis.issues.push(`Check ${checkId} is not an object`);
      return checkAnalysis;
    }

    // Analyze available fields
    const availableFields = Object.keys(checkData);
    checkAnalysis.structure.availableFields = availableFields;
    checkAnalysis.structure.hasData = availableFields.length > 0;

    // Check for expected fields based on format
    const expectedFields = dataFormat === 'cumulative' 
      ? ['isChecked', 'comment', 'lastUpdated']
      : ['isCompleted', 'isChecked', 'selectedPoints'];

    const missingFields = expectedFields.filter(field => !availableFields.includes(field));
    checkAnalysis.structure.missingFields = missingFields;

    // Calculate completeness
    const completeness = expectedFields.length > 0 
      ? ((expectedFields.length - missingFields.length) / expectedFields.length) * 100
      : 0;
    checkAnalysis.structure.dataCompleteness = completeness;

    // Analyze field types and quality
    availableFields.forEach(field => {
      const value = checkData[field];
      const type = typeof value;
      
      checkAnalysis.patterns.fieldTypes[field] = type;
      
      // Quality checks
      if (field === 'comment' && type === 'string') {
        checkAnalysis.patterns.dataQuality.hasComment = value.trim().length > 0;
        checkAnalysis.patterns.dataQuality.commentLength = value.length;
      }
      
      if (field === 'images' && Array.isArray(value)) {
        checkAnalysis.patterns.dataQuality.hasImages = value.length > 0;
        checkAnalysis.patterns.dataQuality.imageCount = value.length;
      }
    });

    return checkAnalysis;
  }

  // Extract category information
  extractCategoryInfo(categoryData) {
    return {
      hasName: !!categoryData.categoryName,
      hasDescription: !!categoryData.description,
      hasCode: !!categoryData.categoryCode,
      additionalFields: Object.keys(categoryData).filter(key => 
        !['controlItems', 'categoryName', 'description', 'categoryCode'].includes(key)
      )
    };
  }

  // Extract control item information
  extractControlItemInfo(controlItemData) {
    return {
      hasName: !!controlItemData.controlItemName,
      hasTranslation: !!controlItemData.controlItemTranslation,
      hasDescription: !!controlItemData.description,
      additionalFields: Object.keys(controlItemData).filter(key => 
        !['checks', 'controlItemName', 'controlItemTranslation', 'description'].includes(key)
      )
    };
  }

  // Extract check information
  extractCheckInfo(checkData) {
    return {
      hasComment: !!(checkData.comment && checkData.comment.trim()),
      hasImages: !!(checkData.images && checkData.images.length > 0),
      hasOriginalCheck: !!checkData.originalCheck,
      hasTimestamp: !!(checkData.lastUpdated || checkData.modifiedAt),
      additionalFields: Object.keys(checkData).filter(key => 
        !['isChecked', 'isCompleted', 'comment', 'images', 'originalCheck', 'lastUpdated', 'modifiedAt', 'selectedPoints'].includes(key)
      )
    };
  }

  // Detect data format
  detectDataFormat(data) {
    // Simple format detection - could be enhanced
    const firstCategoryKey = Object.keys(data)[0];
    if (!firstCategoryKey) return 'empty';
    
    const firstCategory = data[firstCategoryKey];
    if (!firstCategory?.controlItems) return 'invalid';
    
    const firstControlItemKey = Object.keys(firstCategory.controlItems)[0];
    if (!firstControlItemKey) return 'invalid';
    
    const firstControlItem = firstCategory.controlItems[firstControlItemKey];
    if (!firstControlItem?.checks) return 'invalid';
    
    const firstCheckKey = Object.keys(firstControlItem.checks)[0];
    if (!firstCheckKey) return 'invalid';
    
    const firstCheck = firstControlItem.checks[firstCheckKey];
    
    if (firstCheck.hasOwnProperty('isChecked') && firstCheck.hasOwnProperty('lastUpdated')) {
      return 'cumulative';
    } else if (firstCheck.hasOwnProperty('isCompleted')) {
      return 'legacy';
    }
    
    return 'unknown';
  }

  // Update pattern analysis
  updatePatterns(patterns, categoryAnalysis) {
    // Update category patterns
    const categoryName = categoryAnalysis.structure.categoryInfo.hasName ? 'named' : 'unnamed';
    patterns.categoryPatterns[categoryName] = (patterns.categoryPatterns[categoryName] || 0) + 1;
    
    // Update control item patterns
    Object.values(categoryAnalysis.structure.controlItems).forEach(controlItem => {
      const hasName = controlItem.controlItemInfo.hasName ? 'named' : 'unnamed';
      patterns.controlItemPatterns[hasName] = (patterns.controlItemPatterns[hasName] || 0) + 1;
    });
  }

  // Update control item patterns
  updateControlItemPatterns(patterns, controlItemAnalysis) {
    // Track check completion patterns
    const totalChecks = controlItemAnalysis.checkCount;
    const failedChecks = controlItemAnalysis.failedChecks;
    
    if (totalChecks > 0) {
      const failureRate = Math.round((failedChecks / totalChecks) * 100);
      const rateCategory = failureRate === 0 ? 'perfect' : 
                          failureRate < 25 ? 'low_failure' :
                          failureRate < 75 ? 'medium_failure' : 'high_failure';
      
      patterns.completeness[rateCategory] = (patterns.completeness[rateCategory] || 0) + 1;
    }
  }

  // Update check patterns
  updateCheckPatterns(patterns, checkAnalysis) {
    // Track data quality patterns
    if (checkAnalysis.patterns.dataQuality.hasComment) {
      patterns.dataTypes.with_comments = (patterns.dataTypes.with_comments || 0) + 1;
    } else {
      patterns.dataTypes.without_comments = (patterns.dataTypes.without_comments || 0) + 1;
    }
    
    if (checkAnalysis.patterns.dataQuality.hasImages) {
      patterns.dataTypes.with_images = (patterns.dataTypes.with_images || 0) + 1;
    } else {
      patterns.dataTypes.without_images = (patterns.dataTypes.without_images || 0) + 1;
    }
  }

  // Calculate integrity scores
  calculateIntegrityScores(analysis) {
    let score = 100;
    
    // Deduct for structural issues
    const structuralIssues = analysis.dataIntegrity.issues.length;
    score -= structuralIssues * 10;
    
    // Check for empty categories
    const emptyCategories = Object.values(analysis.structureMap).filter(cat => !cat.hasData).length;
    if (emptyCategories > 0) {
      analysis.dataIntegrity.hasEmptyCategories = true;
      score -= emptyCategories * 5;
    }
    
    // Check for empty control items
    let emptyControlItems = 0;
    Object.values(analysis.structureMap).forEach(category => {
      Object.values(category.controlItems).forEach(controlItem => {
        if (!controlItem.hasData) {
          emptyControlItems++;
        }
      });
    });
    
    if (emptyControlItems > 0) {
      analysis.dataIntegrity.hasEmptyControlItems = true;
      score -= emptyControlItems * 3;
    }
    
    analysis.dataIntegrity.consistencyScore = Math.max(0, score);
  }

  // Calculate quality scores
  calculateQualityScores(analysis, data) {
    // Completeness score
    const totalPossibleFields = analysis.totalChecks * 5; // Assume 5 key fields per check
    let actualFields = 0;
    
    Object.values(data).forEach(category => {
      if (category.controlItems) {
        Object.values(category.controlItems).forEach(controlItem => {
          if (controlItem.checks) {
            Object.values(controlItem.checks).forEach(check => {
              actualFields += Object.keys(check).length;
            });
          }
        });
      }
    });
    
    analysis.dataQuality.completenessScore = totalPossibleFields > 0 ? 
      Math.min(100, (actualFields / totalPossibleFields) * 100) : 0;
    
    // Richness score (based on optional fields)
    let richnessPoints = 0;
    let totalChecks = 0;
    
    Object.values(data).forEach(category => {
      if (category.controlItems) {
        Object.values(category.controlItems).forEach(controlItem => {
          if (controlItem.checks) {
            Object.values(controlItem.checks).forEach(check => {
              totalChecks++;
              if (check.comment && check.comment.trim()) richnessPoints += 2;
              if (check.images && check.images.length > 0) richnessPoints += 3;
              if (check.originalCheck) richnessPoints += 1;
              if (check.lastUpdated) richnessPoints += 1;
            });
          }
        });
      }
    });
    
    analysis.dataQuality.richnessScore = totalChecks > 0 ? 
      Math.min(100, (richnessPoints / (totalChecks * 7)) * 100) : 0;
    
    // Consistency score (from integrity)
    analysis.dataQuality.consistencyScore = analysis.dataIntegrity.consistencyScore;
    
    // Overall score (weighted average)
    analysis.dataQuality.overallScore = Math.round(
      (analysis.dataQuality.completenessScore * 0.4) +
      (analysis.dataQuality.richnessScore * 0.3) +
      (analysis.dataQuality.consistencyScore * 0.3)
    );
  }

  // Finalize analysis with additional insights
  finalizeAnalysis(analysis) {
    // Add insights
    analysis.insights = this.generateInsights(analysis);
    
    // Add recommendations
    analysis.recommendations = this.generateRecommendations(analysis);
    
    return analysis;
  }

  // Generate insights from analysis
  generateInsights(analysis) {
    const insights = [];
    
    if (analysis.failedChecks === 0 && analysis.totalChecks > 0) {
      insights.push({
        type: 'success',
        message: 'Perfect audit - all checks passed',
        impact: 'positive'
      });
    }
    
    if (analysis.failedChecks > analysis.passedChecks) {
      insights.push({
        type: 'warning',
        message: 'More failed checks than passed checks',
        impact: 'negative'
      });
    }
    
    if (analysis.dataQuality.overallScore > 80) {
      insights.push({
        type: 'info',
        message: 'High data quality detected',
        impact: 'positive'
      });
    }
    
    if (analysis.dataQuality.overallScore < 50) {
      insights.push({
        type: 'warning',
        message: 'Low data quality may affect transformation accuracy',
        impact: 'negative'
      });
    }
    
    return insights;
  }

  // Generate recommendations
  generateRecommendations(analysis) {
    const recommendations = [];
    
    if (analysis.dataIntegrity.hasEmptyCategories) {
      recommendations.push({
        priority: 'medium',
        action: 'Review empty categories',
        description: 'Some categories contain no audit data'
      });
    }
    
    if (analysis.dataQuality.richnessScore < 30) {
      recommendations.push({
        priority: 'low',
        action: 'Improve data richness',
        description: 'Consider adding more detailed comments and supporting images'
      });
    }
    
    if (analysis.dataIntegrity.consistencyScore < 70) {
      recommendations.push({
        priority: 'high',
        action: 'Fix data consistency issues',
        description: 'Address structural inconsistencies in the audit data'
      });
    }
    
    return recommendations;
  }

  // Get transformation summary
  getTransformationSummary(originalData, transformedData, cacheStats, transformerStats) {
    this.analysisStats.summariesGenerated++;
    
    const originalAnalysis = this.analyzeDataStructure(originalData);
    const validationResult = this.validateTransformedData(transformedData);
    
    return {
      timestamp: new Date().toISOString(),
      original: originalAnalysis,
      transformed: {
        fieldResponsesCount: transformedData.length,
        validResponses: validationResult.validCount,
        invalidResponses: validationResult.errorCount,
        warnings: validationResult.warningCount,
        averageQuality: validationResult.averageQuality
      },
      transformationRatio: originalAnalysis.totalChecks > 0 ? 
        (transformedData.length / originalAnalysis.totalChecks * 100).toFixed(2) + '%' : '0%',
      failureTransformationRatio: originalAnalysis.failedChecks > 0 ?
        (transformedData.length / originalAnalysis.failedChecks * 100).toFixed(2) + '%' : 'N/A',
      success: transformedData.length > 0 || originalAnalysis.failedChecks === 0,
      isIndependent: true,
      performance: {
        cacheHitRate: cacheStats.hitRate,
        transformationSuccessRate: transformerStats.totalTransformations > 0 ? 
          (transformerStats.successfulTransformations / transformerStats.totalTransformations * 100).toFixed(2) + '%' : '0%'
      },
      quality: {
        inputQuality: originalAnalysis.dataQuality.overallScore,
        outputQuality: validationResult.averageQuality,
        qualityImprovement: validationResult.averageQuality - originalAnalysis.dataQuality.overallScore
      }
    };
  }

  // Validate transformed data (simplified version for summary)
  validateTransformedData(transformedData) {
    // Simplified validation for summary purposes
    let validCount = 0;
    let errorCount = 0;
    let warningCount = 0;
    let totalQuality = 0;
    
    transformedData.forEach(response => {
      // Basic validation
      const hasRequiredFields = response.category_id && response.control_item_id && 
                               response.check_id && response.field_label && 
                               response.selected_points !== undefined;
      
      if (hasRequiredFields) {
        validCount++;
        
        // Calculate simple quality score
        let quality = 70; // Base score
        if (response.remark && response.remark.trim()) quality += 10;
        if (response.image_1_url || response.image_2_url || response.image_3_url) quality += 15;
        if (response.additional_data && Object.keys(response.additional_data).length > 0) quality += 5;
        
        totalQuality += quality;
      } else {
        errorCount++;
      }
      
      // Check for warnings
      if (response.selected_points < 0) warningCount++;
      if (!response.remark || response.remark.trim() === '') warningCount++;
    });
    
    return {
      validCount,
      errorCount,
      warningCount,
      averageQuality: transformedData.length > 0 ? Math.round(totalQuality / transformedData.length) : 0
    };
  }

  // Get analysis statistics
  getStats() {
    return {
      ...this.analysisStats,
      historySize: this.analysisHistory.length,
      lastAnalysis: this.analysisHistory.length > 0 ? 
        this.analysisHistory[this.analysisHistory.length - 1].timestamp : null
    };
  }

  // Get analysis history
  getAnalysisHistory(limit = 10) {
    return this.analysisHistory.slice(-limit);
  }

  // Reset analysis statistics
  resetStats() {
    this.analysisStats = {
      totalAnalyses: 0,
      dataStructuresAnalyzed: 0,
      summariesGenerated: 0,
      reportsCreated: 0
    };
    console.log('📊 Analysis statistics reset');
  }

  // Export analyzer data
  export() {
    return {
      exportedAt: new Date().toISOString(),
      stats: this.getStats(),
      recentHistory: this.getAnalysisHistory(5)
    };
  }

  // Reset analyzer
  reset() {
    this.resetStats();
    this.analysisHistory = [];
    console.log('🔄 Analyzer reset complete');
  }
}